import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import type { DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { patch, post, getTodos } from '../../nucleo/api';
import { useSessao, veTodasAsEmpresas, type DadosSessao } from '../../nucleo/sessao';
import { CamposForm, GRADE_FORM, useEstadoForm } from './form';
import { Historico } from './Historico';
import { humanizar } from '../../nucleo/rotulos';
import { formatarNumero, type Sequencia } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

const CODIGOS_USADOS = ['fatura', 'conta_pagar', 'pedido_compra', 'contrato'];

/**
 * Review r42 segurança (B2): a sequência do workspace (sem empresa) vale para todas as empresas; só quem vê todas a edita
 * (o servidor recusa com `permissao.sequencia_exige_todas`). Para os outros, ela abre só para leitura.
 */
export const podeEditarSequencia = (s: { pode(permissao: string): boolean; empresas?: DadosSessao['empresas'] }, sequencia: Pick<Sequencia, 'empresa_id'> | null) =>
  s.pode('configuracoes.editar') && (!sequencia || !!sequencia.empresa_id || veTodasAsEmpresas(s));

/** Review r43 spec (B1): a empresa da sequência nova de quem não vê todas: a atual do seletor ou a primeira permitida. */
export const empresaInicialDaSequencia = (s: Pick<DadosSessao, 'empresas'>) => s.empresas?.atual ?? s.empresas?.opcoes[0]?.id ?? null;

function FormSequencia({ sequencia, aoSalvar }: { sequencia: Sequencia | null; aoSalvar: () => Promise<unknown> }) {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const editando = !!sequencia;
  const podeEditar = podeEditarSequencia(s, sequencia);
  // Review r43 spec (B1): quem não vê todas as empresas nunca salva a sequência do workspace (sem empresa): a nova nasce na
  // empresa atual (ou na primeira permitida) e a empresa é obrigatória, escolhida entre as dele, sem "Todas as empresas".
  const veTodas = veTodasAsEmpresas(s);
  const empresaInicial = empresaInicialDaSequencia(s);
  const inicial = {
    codigo: sequencia?.codigo ?? '',
    empresa_id: sequencia?.empresa_id ?? (veTodas ? null : empresaInicial),
    prefixo: sequencia?.prefixo ?? '',
    digitos: sequencia?.digitos ?? 5,
    proximo: sequencia?.proximo_efetivo ?? 1,
    reinicio_anual: sequencia?.reinicio_anual ?? false,
  };
  const f = useEstadoForm<Record<string, unknown>>(inicial);
  const bloqueado = !podeEditar;

  const defs: DefCampo[] = [
    ...(editando
      ? []
      : ([
          { nome: 'codigo', rotulo: t('sequencias.codigo'), tipo: 'texto', obrigatorio: true, largura: 'meia', ajuda: t('sequencias.codigoDica'), placeholder: CODIGOS_USADOS[0] },
          veTodas
            ? {
                nome: 'empresa_id',
                rotulo: t('sequencias.empresa'),
                tipo: 'busca',
                largura: 'meia',
                ajuda: t('sequencias.todasEmpresas'),
                busca: { caminho: '/configuracoes/empresas', rotulo: (i) => String(i.nome_fantasia || i.razao_social) },
              }
            : {
                nome: 'empresa_id',
                rotulo: t('sequencias.empresa'),
                tipo: 'selecao',
                obrigatorio: true,
                largura: 'meia',
                opcoes: (s.empresas?.opcoes ?? []).map((e) => ({ valor: e.id, rotulo: e.nome })),
              },
        ] satisfies DefCampo[])),
    { nome: 'prefixo', rotulo: t('sequencias.prefixo'), tipo: 'texto', largura: 'meia', ajuda: t('sequencias.prefixoDica'), desabilitado: bloqueado },
    { nome: 'digitos', rotulo: t('sequencias.digitos'), tipo: 'numero', obrigatorio: true, min: 1, max: 12, passo: 1, largura: 'terco', desabilitado: bloqueado },
    { nome: 'proximo', rotulo: t('sequencias.proximo'), tipo: 'numero', obrigatorio: true, min: editando ? inicial.proximo : 1, passo: 1, largura: 'meia', ajuda: t('sequencias.proximoDica'), desabilitado: bloqueado },
    { nome: 'reinicio_anual', rotulo: t('sequencias.reinicio'), tipo: 'booleano', largura: 'meia', desabilitado: bloqueado },
  ];

  const ano = new Date().getFullYear();
  const digitos = Math.min(12, Math.max(1, Number(f.valores.digitos) || 1));
  const previa = formatarNumero(String(f.valores.prefixo ?? ''), digitos, Math.max(1, Number(f.valores.proximo) || 1), ano);

  return (
    <form
      id="form-sequencia"
      noValidate
      className={GRADE_FORM}
      onSubmit={(ev) =>
        f.enviar(ev, defs, tn('comum.obrigatorio'), async (v) => {
          if (!Number.isInteger(Number(v.proximo)) || Number(v.proximo) < 1) throw new Error(t('sequencias.proximoInvalido'));
          if (!Number.isInteger(Number(v.digitos)) || Number(v.digitos) < 1 || Number(v.digitos) > 12) throw new Error(t('sequencias.digitosInvalido'));
          const comum = { prefixo: v.prefixo ?? '', digitos: Number(v.digitos), reinicio_anual: !!v.reinicio_anual };
          if (sequencia) await patch(`/configuracoes/sequencias/${sequencia.id}`, { ...comum, ...(Number(v.proximo) !== inicial.proximo ? { proximo: Number(v.proximo) } : {}) });
          else await post('/configuracoes/sequencias', { ...comum, codigo: String(v.codigo ?? '').trim(), empresa_id: v.empresa_id || null, proximo: Number(v.proximo) });
          await aoSalvar();
        })
      }
    >
      {sequencia && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm sm:col-span-6">
          <p>
            <span className="text-tinta-3">{t('sequencias.codigo')}: </span>
            <span className="numero font-medium">{sequencia.codigo}</span>
          </p>
          <p>
            <span className="text-tinta-3">{t('sequencias.empresa')}: </span>
            {sequencia.empresa_nome ?? t('sequencias.todasEmpresas')}
          </p>
        </div>
      )}
      <CamposForm defs={defs} valores={f.valores} erros={f.erros} erroGeral={f.erroGeral} aoMudar={f.definir} />
      <div className="rounded-xl border border-borda bg-sup-2 p-4 sm:col-span-6">
        <p className="kicker">{t('sequencias.previa')}</p>
        <p className="numero mt-1 text-xl font-semibold" aria-live="polite">
          {previa}
        </p>
      </div>
    </form>
  );
}

/** M13.2: sequências de numeração com a próxima numeração formatada. */
export function Sequencias() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<string | 'nova' | null>(null);
  const q = useQuery({ queryKey: ['configuracoes', 'sequencias'], queryFn: () => getTodos<Sequencia>('/configuracoes/sequencias') });
  const atual = aberto && aberto !== 'nova' ? (q.data?.itens.find((x) => x.id === aberto) ?? null) : null;
  const podeEditar = s.pode('configuracoes.editar');

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {podeEditar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
            {t('acoes.novaSequencia')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Cartao titulo={t('sequencias.titulo')} semPadding>
        <p className="border-b border-borda px-4 py-3 text-sm text-tinta-3">{t('sequencias.ajuda')}</p>
        <Estado consulta={q}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio texto={t('sequencias.vazio')} />
            ) : (
              <ul className="divide-y divide-borda">
                {d.itens.map((x) => (
                  <li key={x.id}>
                    <button type="button" onClick={() => setAberto(x.id)} className="flex w-full min-w-0 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-sup-2">
                      <span className="flex min-w-0 flex-1 basis-44 flex-col">
                        <span className="truncate font-medium" title={x.codigo}>{humanizar(x.codigo)}</span>
                        <TextoCortado className="text-xs text-tinta-3">{x.empresa_nome ?? t('sequencias.todasEmpresas')}</TextoCortado>
                      </span>
                      <span className="flex flex-col text-xs text-tinta-3">
                        {t('sequencias.proxima')}
                        <span className="numero text-sm text-tinta-1">{x.proxima_numeracao}</span>
                      </span>
                      <Selo cor={x.reinicio_anual ? 'ciano' : 'neutro'}>{x.reinicio_anual ? t('sequencias.sim') : t('sequencias.nao')}</Selo>
                    </button>
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      </Cartao>

      <Historico entidade="configuracoes.sequencia" titulo={t('sequencias.historico')} />

      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? t('sequencias.editarTitulo') : t('sequencias.novaTitulo')}
        rodape={
          podeEditarSequencia(s, atual) && (
            <>
              <Botao variante="fantasma" onClick={() => setAberto(null)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-sequencia">
                {tn('comum.salvar')}
              </Botao>
            </>
          )
        }
      >
        <FormSequencia
          key={atual?.id ?? 'nova'}
          sequencia={atual}
          aoSalvar={async () => {
            await Promise.all([qc.invalidateQueries({ queryKey: ['configuracoes', 'sequencias'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] })]);
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>
    </div>
  );
}
